import { useState, useEffect } from 'react';
import { Card, Row, Col, Select, Button, Table, Space, message, Empty, Spin, Modal } from 'antd';
import { FileTextOutlined, PrinterOutlined, EyeOutlined, FilePdfOutlined, ReloadOutlined } from '@ant-design/icons';
import apiClient from '../../../api/apiClient';
import { getCicloActual, getAniosAcademicos } from '../../../utils/cicloEscolar';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

const { Option } = Select;

const BoletaCalificaciones = () => {

  // Estados para filtros
  const [filtros, setFiltros] = useState({
    cicloEscolar: getCicloActual(),
    idGrado: null,
    idSeccion: null,
    idJornada: null
  });

  // Estados para catálogos
  const [grados, setGrados] = useState([]);
  const [secciones, setSecciones] = useState([]);
  const [jornadas, setJornadas] = useState([]);

  // Estados para estudiantes y boletas
  const [estudiantes, setEstudiantes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingCatalogos, setLoadingCatalogos] = useState(true);

  // Estado para modal de boleta individual
  const [modalVisible, setModalVisible] = useState(false);
  const [boletaSeleccionada, setBoletaSeleccionada] = useState(null);
  const [loadingBoleta, setLoadingBoleta] = useState(false);

  // Cargar catálogos al montar el componente
  useEffect(() => {
    cargarCatalogos();
  }, []);

  const cargarCatalogos = async () => {
    setLoadingCatalogos(true);
    try {
      const [gradosRes, seccionesRes, jornadasRes] = await Promise.all([
        apiClient.get('/grados'),
        apiClient.get('/secciones'),
        apiClient.get('/jornadas')
      ]);

      if (gradosRes.data.success) setGrados(gradosRes.data.data);
      if (seccionesRes.data.success) setSecciones(seccionesRes.data.data);
      if (jornadasRes.data.success) setJornadas(jornadasRes.data.data);
    } catch (error) {
      message.error('Error al cargar los catálogos');
    } finally {
      setLoadingCatalogos(false);
    }
  };

  const cargarEstudiantes = async () => {
    // Validar que todos los filtros estén seleccionados
    if (!filtros.cicloEscolar || !filtros.idGrado || !filtros.idSeccion || !filtros.idJornada) {
      message.warning('Debes seleccionar todos los filtros');
      return;
    }

    setLoading(true);
    try {
      const response = await apiClient.get('/boleta-calificaciones/estudiantes', {
        params: filtros
      });

      if (response.data.success) {
        setEstudiantes(response.data.data || []);
        if (response.data.data.length === 0) {
          message.info('No se encontraron estudiantes con los filtros seleccionados');
        }
      }
    } catch (error) {
      message.error('Error al cargar los estudiantes');
      setEstudiantes([]);
    } finally {
      setLoading(false);
    }
  };

  const verBoleta = async (idAlumno) => {
    setLoadingBoleta(true);
    setModalVisible(true);
    setBoletaSeleccionada(null);

    try {
      const response = await apiClient.get(
        `/boleta-calificaciones/calificaciones/${idAlumno}`,
        { params: filtros }
      );

      if (response.data.success) {
        setBoletaSeleccionada(response.data.data);
      }
    } catch (error) {
      message.error('Error al cargar la boleta');
      setModalVisible(false);
    } finally {
      setLoadingBoleta(false);
    }
  };

  const NOMBRE_COLEGIO = 'Colegio Privado Urbano Mixto Candelaria';

  // Carga el logo del colegio como base64 para incrustarlo en el PDF
  const cargarLogoBase64 = () =>
    new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        canvas.getContext('2d').drawImage(img, 0, 0);
        resolve(canvas.toDataURL('image/png'));
      };
      img.onerror = () => resolve(null); // si falla, continúa sin logo
      img.src = '/LogoSinFondo.png';
    });

  // Dibuja una boleta completa en el doc a partir de startY. Devuelve el Y donde terminó.
  const dibujarBoleta = (doc, dataBoleta, startY, pageWidth, margin, logoBase64) => {
    let y = startY;
    const logoSize = 36;
    const centerX = pageWidth / 2;

    // Logo arriba a la derecha — subido 6mm para no tapar el texto de jornada
    if (logoBase64) {
      doc.addImage(logoBase64, 'PNG', pageWidth - margin - logoSize, y - 6, logoSize, logoSize);
    }

    // Encabezado centrado (solo avanza y por la altura del texto, no del logo)
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.text(NOMBRE_COLEGIO, centerX, y + 5, { align: 'center' });

    doc.setFontSize(11);
    doc.text('BOLETA DE CALIFICACIONES', centerX, y + 11, { align: 'center' });

    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.text(`Ciclo Escolar ${dataBoleta.estudiante.CicloEscolar}`, centerX, y + 17, { align: 'center' });

    y += 21; // solo el bloque de texto del encabezado

    // Nombre: Apellidos primero, luego Nombres
    doc.setFontSize(9.5);
    doc.setFont('helvetica', 'bold');
    doc.text(`${dataBoleta.estudiante.Apellidos} ${dataBoleta.estudiante.Nombres}`, margin, y);

    y += 5;
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.text(`Código: ${dataBoleta.estudiante.Codigo}`, margin, y);
    doc.text(`Grado: ${dataBoleta.estudiante.NombreGrado} ${dataBoleta.estudiante.NombreSeccion}  |  Jornada: ${dataBoleta.estudiante.NombreJornada}`, pageWidth - margin, y, { align: 'right' });

    y += 4;
    // Línea separadora azul
    doc.setDrawColor(41, 128, 185);
    doc.setLineWidth(0.3);
    doc.line(margin, y, pageWidth - margin, y);
    y += 2;

    // La tabla empieza después del texto O después del logo, lo que sea más bajo
    const tableStartY = Math.max(y, startY + logoSize + 2);

    // Datos de la tabla
    const tableData = dataBoleta.cursos.map(curso => {
      const unidades = [1, 2, 3, 4].map(numUnidad => {
        const unidad = curso.unidades.find(u => u.NumeroUnidad === numUnidad);
        if (!unidad || unidad.NotaFinal === null || unidad.NotaFinal === undefined) return '-';
        return unidad.NotaFinal.toString();
      });
      const prom = (curso.promedio === null || curso.promedio === undefined || curso.promedio === '')
        ? '-' : curso.promedio.toString();
      return [curso.NombreCurso, ...unidades, prom];
    });

    // Fila de promedio por unidad
    const promsUnidad = [1, 2, 3, 4].map((numUnidad) => {
      const notas = dataBoleta.cursos
        .map(c => c.unidades.find(u => u.NumeroUnidad === numUnidad)?.NotaFinal)
        .filter(n => n !== null && n !== undefined);
      if (notas.length === 0) return null;
      return Math.round((notas.reduce((a, b) => a + b, 0) / notas.length) * 100) / 100;
    });
    const promsCursos = dataBoleta.cursos
      .map(c => c.promedio)
      .filter(p => p !== null && p !== undefined && p !== '');
    const promFilaGeneral = promsCursos.length > 0
      ? Math.round((promsCursos.reduce((a, b) => a + Number(b), 0) / promsCursos.length) * 100) / 100
      : null;

    const filaBg = [214, 228, 255];
    const celda = (valor) => ({
      content: valor !== null ? String(valor) : '-',
      styles: {
        fontStyle: 'bold',
        halign: 'center',
        fillColor: filaBg,
        textColor: valor === null ? [150, 150, 150] : valor >= 60 ? [22, 119, 255] : [255, 77, 79],
      },
    });

    tableData.push([
      { content: 'Promedio por Unidad', styles: { fontStyle: 'bold', fillColor: filaBg, textColor: [30, 30, 30] } },
      ...promsUnidad.map(celda),
      celda(promFilaGeneral),
    ]);

    autoTable(doc, {
      startY: tableStartY,
      head: [['Curso', 'U1', 'U2', 'U3', 'U4', 'Prom']],
      body: tableData,
      theme: 'grid',
      headStyles: { fillColor: [41, 128, 185], textColor: 255, fontStyle: 'bold', halign: 'center', fontSize: 9 },
      columnStyles: {
        0: { halign: 'left', cellWidth: 100 },
        1: { halign: 'center', cellWidth: 17 },
        2: { halign: 'center', cellWidth: 17 },
        3: { halign: 'center', cellWidth: 17 },
        4: { halign: 'center', cellWidth: 17 },
        5: { halign: 'center', cellWidth: 20, fontStyle: 'bold', fillColor: [240, 240, 240] }
      },
      styles: { fontSize: 9, cellPadding: 2.5, lineColor: [200, 200, 200], lineWidth: 0.1 },
      margin: { left: margin, right: margin }
    });

    const promedioY = doc.lastAutoTable.finalY + 5;
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    const promedioTexto = (dataBoleta.promedioGeneral === null || dataBoleta.promedioGeneral === undefined || dataBoleta.promedioGeneral === '')
      ? 'PROMEDIO GENERAL: - puntos'
      : `PROMEDIO GENERAL: ${dataBoleta.promedioGeneral} puntos`;
    doc.text(promedioTexto, centerX, promedioY, { align: 'center' });

    return promedioY + 4;
  };

  const generarPDFBoleta = async (dataBoleta) => {
    try {
      const logoBase64 = await cargarLogoBase64();
      const doc = new jsPDF(); // portrait
      const pageWidth = doc.internal.pageSize.width;
      const pageHeight = doc.internal.pageSize.height;
      const margin = 12;

      dibujarBoleta(doc, dataBoleta, margin, pageWidth, margin, logoBase64);

      doc.setFontSize(7);
      doc.setFont('helvetica', 'italic');
      doc.text(`Generado el ${new Date().toLocaleDateString('es-GT')}`, pageWidth / 2, pageHeight - 5, { align: 'center' });

      return doc;
    } catch (error) {
      throw error;
    }
  };

  const imprimirBoleta = async (idAlumno) => {
    try {
      message.loading({ content: 'Generando PDF...', key: 'pdf' });

      const response = await apiClient.get(
        `/boleta-calificaciones/calificaciones/${idAlumno}`,
        { params: filtros }
      );

      if (response.data.success) {
        const dataBoleta = response.data.data;

        const doc = await generarPDFBoleta(dataBoleta);

        const fileName = `Boleta_${dataBoleta.estudiante.Codigo}_${dataBoleta.estudiante.Nombres}_${dataBoleta.estudiante.Apellidos}.pdf`;
        doc.save(fileName);

        message.success({ content: 'PDF generado exitosamente', key: 'pdf' });
      } else {
      }
    } catch (error) {
      message.error({ content: 'Error al generar el PDF', key: 'pdf' });
    }
  };

  const imprimirTodasLasBoletas = async () => {
    if (estudiantes.length === 0) {
      message.warning('No hay estudiantes para imprimir');
      return;
    }

    // Portrait: fontSize 9 + cellPadding 2.5 ≈ 8.2mm/fila
    // ALTURA_HEADER = logoSize + 2 (la tabla arranca justo después del logo en el peor caso)
    const MM_POR_FILA = 8.2;
    const ALTURA_HEADER = 36 + 2; // logoSize + 2mm de margen
    const ALTURA_PROMEDIO = 10;

    // Estima la altura total de una boleta en mm según su número de cursos
    const estimarAltura = (dataBoleta) => {
      const numCursos = dataBoleta.cursos.length;
      return ALTURA_HEADER + (numCursos + 1) * MM_POR_FILA + ALTURA_PROMEDIO;
    };

    try {
      message.loading({ content: `Generando PDF con ${estudiantes.length} boletas...`, key: 'pdfMasivo' });
      const logoBase64 = await cargarLogoBase64();

      const idsAlumnos = estudiantes.map(est => est.IdAlumno);
      const payload = {
        cicloEscolar: filtros.cicloEscolar,
        idGrado: filtros.idGrado,
        idSeccion: filtros.idSeccion,
        idJornada: filtros.idJornada,
        estudiantes: idsAlumnos
      };

      const response = await apiClient.post('/boleta-calificaciones/lote', payload);

      if (response.data.success) {
        const boletas = response.data.data;

        if (!boletas || boletas.length === 0) {
          message.warning('No se recibieron boletas del backend');
          return;
        }

        const doc = new jsPDF(); // portrait
        const pageWidth = doc.internal.pageSize.width;   // 216mm
        const pageHeight = doc.internal.pageSize.height; // 279mm
        const margin = 12;
        const margenInferior = 12;
        const separadorAltura = 8;

        const dibujarFooter = () => {
          doc.setFontSize(7);
          doc.setFont('helvetica', 'italic');
          doc.text(`Generado el ${new Date().toLocaleDateString('es-GT')}`, pageWidth / 2, pageHeight - 5, { align: 'center' });
        };

        let currentY = margin;

        for (let i = 0; i < boletas.length; i++) {
          const dataBoleta = boletas[i];

          if (i > 0) {
            const alturaEstimada = estimarAltura(dataBoleta);
            const espacioRestante = pageHeight - currentY - margenInferior - separadorAltura;

            if (alturaEstimada <= espacioRestante) {
              // Cabe en la misma página: dibujar separador
              currentY += separadorAltura / 2;
              doc.setDrawColor(180, 180, 180);
              doc.setLineWidth(0.4);
              doc.line(margin, currentY, pageWidth - margin, currentY);
              currentY += separadorAltura / 2;
            } else {
              // No cabe: nueva página
              dibujarFooter();
              doc.addPage();
              currentY = margin;
            }
          }

          currentY = dibujarBoleta(doc, dataBoleta, currentY, pageWidth, margin, logoBase64);
        }

        dibujarFooter();

        const fileName = `Boletas_${filtros.cicloEscolar}_Grado${filtros.idGrado}_Seccion${filtros.idSeccion}.pdf`;
        doc.save(fileName);
        message.success({ content: `PDF generado con ${boletas.length} boletas`, key: 'pdfMasivo' });
      }
    } catch (error) {
      message.error({ content: 'Error al generar el PDF masivo', key: 'pdfMasivo' });
    }
  };

  const columnasEstudiantes = [
    {
      title: 'Código',
      dataIndex: 'Codigo',
      key: 'Codigo',
      width: 120
    },
    {
      title: 'Nombre Completo',
      key: 'nombreCompleto',
      render: (_, record) => `${record.Nombres} ${record.Apellidos}`
    },
    {
      title: 'Grado',
      dataIndex: 'NombreGrado',
      key: 'NombreGrado',
      width: 150
    },
    {
      title: 'Sección',
      dataIndex: 'NombreSeccion',
      key: 'NombreSeccion',
      width: 100,
      align: 'center'
    },
    {
      title: 'Jornada',
      dataIndex: 'NombreJornada',
      key: 'NombreJornada',
      width: 120
    },
    {
      title: 'Acciones',
      key: 'acciones',
      width: 180,
      fixed: 'right',
      render: (_, record) => (
        <Space>
          <Button
            type="primary"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => verBoleta(record.IdAlumno)}
          >
            Ver
          </Button>
          <Button
            size="small"
            icon={<FilePdfOutlined />}
            onClick={() => imprimirBoleta(record.IdAlumno)}
          >
            PDF
          </Button>
        </Space>
      )
    }
  ];

  return (
    <div style={{ padding: '24px' }}>
      <Card
        title={
          <span>
            <FileTextOutlined /> Boleta de Calificaciones
          </span>
        }
      >
        {/* Filtros */}
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={24} sm={12} md={6}>
            <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>
              Año Escolar
            </label>
            <Select
              style={{ width: '100%' }}
              placeholder="Seleccionar año"
              value={filtros.cicloEscolar}
              onChange={(value) => setFiltros(prev => ({ ...prev, cicloEscolar: value }))}
            >
              {getAniosAcademicos().map(y => <Option key={y} value={y}>{y}</Option>)}
            </Select>
          </Col>

          <Col xs={24} sm={12} md={6}>
            <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>
              Grado
            </label>
            <Select
              style={{ width: '100%' }}
              placeholder="Seleccionar grado"
              value={filtros.idGrado}
              onChange={(value) => setFiltros(prev => ({ ...prev, idGrado: value }))}
              loading={loadingCatalogos}
              disabled={loadingCatalogos}
            >
              {grados.map(grado => (
                <Option key={grado.IdGrado} value={grado.IdGrado}>
                  {grado.NombreGrado}
                </Option>
              ))}
            </Select>
          </Col>

          <Col xs={24} sm={12} md={6}>
            <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>
              Sección
            </label>
            <Select
              style={{ width: '100%' }}
              placeholder="Seleccionar sección"
              value={filtros.idSeccion}
              onChange={(value) => setFiltros(prev => ({ ...prev, idSeccion: value }))}
              loading={loadingCatalogos}
              disabled={loadingCatalogos}
            >
              {secciones.map(seccion => (
                <Option key={seccion.IdSeccion} value={seccion.IdSeccion}>
                  {seccion.NombreSeccion}
                </Option>
              ))}
            </Select>
          </Col>

          <Col xs={24} sm={12} md={6}>
            <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>
              Jornada
            </label>
            <Select
              style={{ width: '100%' }}
              placeholder="Seleccionar jornada"
              value={filtros.idJornada}
              onChange={(value) => setFiltros(prev => ({ ...prev, idJornada: value }))}
              loading={loadingCatalogos}
              disabled={loadingCatalogos}
            >
              {jornadas.map(jornada => (
                <Option key={jornada.IdJornada} value={jornada.IdJornada}>
                  {jornada.NombreJornada}
                </Option>
              ))}
            </Select>
          </Col>
        </Row>

        {/* Botones de acción */}
        <Row style={{ marginBottom: 16 }}>
          <Col span={24}>
            <Space>
              <Button
                type="primary"
                icon={<ReloadOutlined />}
                onClick={cargarEstudiantes}
                loading={loading}
              >
                Buscar Estudiantes
              </Button>

              <Button
                icon={<PrinterOutlined />}
                onClick={imprimirTodasLasBoletas}
                disabled={estudiantes.length === 0}
              >
                Imprimir Todas las Boletas ({estudiantes.length})
              </Button>
            </Space>
          </Col>
        </Row>

        {/* Tabla de estudiantes */}
        <Table
          columns={columnasEstudiantes}
          dataSource={estudiantes}
          rowKey="IdAlumno"
          loading={loading}
          pagination={{
            pageSize: 20,
            showSizeChanger: true,
            showTotal: (total) => `Total: ${total} estudiantes`
          }}
          locale={{
            emptyText: (
              <Empty
                description="Selecciona los filtros y haz clic en 'Buscar Estudiantes'"
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            )
          }}
          scroll={{ x: 900 }}
        />
      </Card>

      {/* Modal para ver boleta */}
      <Modal
        title={
          <span>
            <FileTextOutlined /> Boleta de Calificaciones
          </span>
        }
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        width={900}
      >
        {loadingBoleta ? (
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <Spin size="large" />
            <p style={{ marginTop: 16 }}>Cargando boleta...</p>
          </div>
        ) : boletaSeleccionada ? (
          <div>
            {/* Información del estudiante */}
            <Card size="small" style={{ marginBottom: 16, backgroundColor: '#f5f5f5' }}>
              <Row gutter={16}>
                <Col span={12}>
                  <strong>Estudiante:</strong> {boletaSeleccionada.estudiante.Nombres} {boletaSeleccionada.estudiante.Apellidos}
                </Col>
                <Col span={12}>
                  <strong>Código:</strong> {boletaSeleccionada.estudiante.Codigo}
                </Col>
                <Col span={8}>
                  <strong>Grado:</strong> {boletaSeleccionada.estudiante.NombreGrado}
                </Col>
                <Col span={8}>
                  <strong>Sección:</strong> {boletaSeleccionada.estudiante.NombreSeccion}
                </Col>
                <Col span={8}>
                  <strong>Jornada:</strong> {boletaSeleccionada.estudiante.NombreJornada}
                </Col>
              </Row>
            </Card>

            {/* Calificaciones por curso */}
            {boletaSeleccionada.cursos.length > 0 ? (
              <>
                {boletaSeleccionada.cursos.map((curso, index) => (
                  <Card
                    key={index}
                    size="small"
                    title={curso.NombreCurso}
                    style={{ marginBottom: 16 }}
                  >
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ backgroundColor: '#fafafa', borderBottom: '2px solid #d9d9d9' }}>
                          <th style={{ padding: 8, textAlign: 'center' }}>Unidad 1</th>
                          <th style={{ padding: 8, textAlign: 'center' }}>Unidad 2</th>
                          <th style={{ padding: 8, textAlign: 'center' }}>Unidad 3</th>
                          <th style={{ padding: 8, textAlign: 'center' }}>Unidad 4</th>
                          <th style={{ padding: 8, textAlign: 'center', backgroundColor: '#e6f7ff' }}>
                            <strong>PROMEDIO</strong>
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          {[1, 2, 3, 4].map(numUnidad => {
                            const unidad = curso.unidades.find(u => u.NumeroUnidad === numUnidad);
                            return (
                              <td key={numUnidad} style={{ padding: 8, textAlign: 'center', border: '1px solid #d9d9d9' }}>
                                {unidad ? unidad.NotaFinal : '-'}
                              </td>
                            );
                          })}
                          <td style={{ padding: 8, textAlign: 'center', border: '1px solid #d9d9d9', backgroundColor: '#e6f7ff' }}>
                            <strong>{curso.promedio}</strong>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </Card>
                ))}

                {/* Promedio por Unidad */}
                {(() => {
                  const promediosUnidad = [1, 2, 3, 4].map((numUnidad) => {
                    const notas = boletaSeleccionada.cursos
                      .map(c => c.unidades.find(u => u.NumeroUnidad === numUnidad)?.NotaFinal)
                      .filter(n => n !== null && n !== undefined);
                    if (notas.length === 0) return null;
                    return Math.round((notas.reduce((a, b) => a + b, 0) / notas.length) * 100) / 100;
                  });

                  const promedioCursos = boletaSeleccionada.cursos
                    .map(c => c.promedio)
                    .filter(p => p !== null && p !== undefined && p !== '');
                  const promedioFila = promedioCursos.length > 0
                    ? Math.round((promedioCursos.reduce((a, b) => a + Number(b), 0) / promedioCursos.length) * 100) / 100
                    : null;

                  const estilo = (valor) => ({
                    padding: 8,
                    textAlign: 'center',
                    border: '1px solid #d9d9d9',
                    color: valor === null ? '#999' : valor >= 60 ? '#1677ff' : '#ff4d4f',
                    fontWeight: 'bold',
                  });

                  return (
                    <Card
                      size="small"
                      title="Promedio por Unidad"
                      style={{ marginBottom: 16, backgroundColor: '#f0f5ff', borderColor: '#adc6ff' }}
                    >
                      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                          <tr style={{ backgroundColor: '#d6e4ff', borderBottom: '2px solid #adc6ff' }}>
                            <th style={{ padding: 8, textAlign: 'center' }}>Unidad 1</th>
                            <th style={{ padding: 8, textAlign: 'center' }}>Unidad 2</th>
                            <th style={{ padding: 8, textAlign: 'center' }}>Unidad 3</th>
                            <th style={{ padding: 8, textAlign: 'center' }}>Unidad 4</th>
                            <th style={{ padding: 8, textAlign: 'center' }}>Promedio</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            {promediosUnidad.map((prom, i) => (
                              <td key={i} style={estilo(prom)}>{prom ?? '-'}</td>
                            ))}
                            <td style={estilo(promedioFila)}>{promedioFila ?? '-'}</td>
                          </tr>
                        </tbody>
                      </table>
                    </Card>
                  );
                })()}

                {/* Promedio General */}
                <Card
                  size="small"
                  style={{ backgroundColor: '#f6ffed', borderColor: '#b7eb8f' }}
                >
                  <div style={{ textAlign: 'center', fontSize: 16 }}>
                    <strong>PROMEDIO GENERAL: {boletaSeleccionada.promedioGeneral} puntos</strong>
                  </div>
                </Card>

                {/* Botón de imprimir */}
                <div style={{ marginTop: 16, textAlign: 'right' }}>
                  <Button
                    type="primary"
                    icon={<FilePdfOutlined />}
                    onClick={() => imprimirBoleta(boletaSeleccionada.estudiante.IdAlumno)}
                  >
                    Generar PDF
                  </Button>
                </div>
              </>
            ) : (
              <Empty description="Este estudiante no tiene calificaciones registradas" />
            )}
          </div>
        ) : null}
      </Modal>
    </div>
  );
};

export default BoletaCalificaciones;
